import { LabReportUploadRequest } from "@9amhealth/openapi";
import {
  NineActions,
  NineButton,
  NineHeading
} from "@9amhealth/wcl/generated/react";
import type { FC } from "react";
import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import FileType from "src/constants/fileType";
import translate from "src/lib/translate";
import DocumentUploader, {
  DocumentUploaderRef
} from "src/ui/components/DocumentUploader/DocumentUploader";
import { ClearButton } from "src/ui/components/PharmacyForm/PharmacyForm";
import Translate from "src/ui/components/Translate/Translate";
import { useDeferredDocumentUpload } from "src/ui/hooks/useDeferredDocumentUpload";
import { v4 as uuidv4 } from "uuid";
import styled from "@emotion/styled";
import { UploadLabResultStepContent } from "./UploadLabResultStepContent";
import { Step, ChatUploadLabResultsCubit } from "./ChatUploadLabResultsCubit";
import { useBlocNext } from "@blac/react";
import {
  HelpLinkButton,
  HelpLinkWrap,
  InfoText,
  StepContent
} from "./components";

const UploadWrapper = styled.div`
  position: relative;
  padding-bottom: var(--space-lg);
`;

const StepOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--color-cream, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-md);
  z-index: 2;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

export const ChatUploadLabResults: FC<{ closeDialog: () => void }> = ({
  closeDialog
}) => {
  const [
    { currentStep, selectedFilesMeta },
    {
      handleSkipLabReportUpload,
      setSelectedFilesMeta,
      setCurrentStep,
      isPdfMeta,
      handleSubmitLabReport,
      syncSelectedFilesMeta
    }
  ] = useBlocNext(ChatUploadLabResultsCubit);

  const documentSessionId = useMemo<string>(() => uuidv4(), []);
  const documentUploaderRef = useRef<DocumentUploaderRef | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const showSkip = useSearchParams()[0].get("showSkip") === "true";

  const {
    documentConfig: baseDocumentConfig,
    handleFileUploadChange,
    handleContinue,
    isUploading,
    hasSelectedFiles
  } = useDeferredDocumentUpload({
    metaFileType: FileType.LAB_RESULT,
    tags: ["lab-report", `document-upload-session:${documentSessionId}`],
    getTitleForDocument: (index, hasFileId) =>
      index === 0 || hasFileId ? translate("chat.labUpload.uploadPdf") : ""
  });

  const documentConfig = useMemo(() => {
    const config = { ...baseDocumentConfig };
    Object.keys(config).forEach((docId) => {
      const docConfig = config[docId];
      if (docConfig && docConfig.tags) {
        config[docId] = {
          ...docConfig,
          tags: [...docConfig.tags, `page-create-time-${docId}`]
        };
      }
    });
    return config;
  }, [baseDocumentConfig]);

  const handleDocumentChange = (files: Record<string, string>) => {
    handleFileUploadChange(files);
    syncSelectedFilesMeta(files);
  };

  const handleDocumentSelect = useCallback(
    (file: File, _values: Record<string, string>, docName: string) => {
      setSelectedFilesMeta(docName, file);
      setTimeout(() => {
        actionsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }, 300);
    },
    []
  );

  const hasNonPdfSelected = useMemo(
    () => Object.values(selectedFilesMeta).some((meta) => !isPdfMeta(meta)),
    [selectedFilesMeta, isPdfMeta]
  );

  const uploadFilesAndShowStep = useCallback(
    async (step: Step, provider?: LabReportUploadRequest["provider"]) => {
      if (!documentUploaderRef.current || !hasSelectedFiles) {
        return;
      }

      const handleShowStep = async (fileIds?: string[]) => {
        if (!fileIds || fileIds.length === 0) {
          return;
        }

        await handleSubmitLabReport(fileIds, provider);
        setCurrentStep(step);
      };

      await handleContinue(
        documentUploaderRef,
        (fileIds) => void handleShowStep(fileIds)
      );
    },
    [handleContinue, hasSelectedFiles, handleSubmitLabReport]
  );

  const handleConfirm = () => {
    if (!hasSelectedFiles) {
      return;
    }

    if (hasNonPdfSelected) {
      setCurrentStep("unsupportedFormat");
      return;
    }

    void uploadFilesAndShowStep(
      "almostThere",
      LabReportUploadRequest.provider.PATIENT
    );
  };

  return (
    <UploadWrapper
      style={{
        overflow: currentStep === "upload" ? undefined : "hidden",
        height: currentStep === "upload" ? undefined : "100%"
      }}
    >
      <StepContent>
        <NineHeading>
          <h3 className="as-h4-large">
            <Translate msg="chat.uploadLabReportPdf.title" />
          </h3>

          <p className="color-c-80">
            <Translate msg="chat.uploadLabReportPdf.subtitle" />
          </p>
        </NineHeading>
        <DocumentUploader
          display={{
            ratio: 0.8,
            showSelectedTitles: false,
            action: "delete"
          }}
          config={{
            fileTypes: [
              "image/jpeg",
              "image/png",
              "application/pdf",
              "image/jpg"
            ],
            files: documentConfig
          }}
          onChange={handleDocumentChange}
          onSelect={handleDocumentSelect}
          deferUpload={true}
          ref={documentUploaderRef}
        />
        <div ref={actionsRef}>
          <NineActions style={{ justifyContent: "center" }}>
            <NineButton
              disabled={hasSelectedFiles && !isUploading ? "false" : "true"}
              onClick={handleConfirm}
            >
              <Translate msg="confirm" />
            </NineButton>
          </NineActions>
        </div>
        <HelpLinkWrap>
          <div>{translate("chat.labUpload.pdfInfo.prompt")}</div>
          <HelpLinkButton
            type="button"
            onClick={() => setCurrentStep("pdfInfo")}
          >
            <Translate msg="chat.labUpload.pdfInfo.link" />
          </HelpLinkButton>
        </HelpLinkWrap>
        {showSkip && (
          <div style={{ textAlign: "center" }}>
            <nine-spacer s="lg" />
            <InfoText>
              {translate("task.skip.question", {
                context: "upload_labs"
              })}
            </InfoText>
            <nine-spacer s="xxxs" />
            <ClearButton
              style={{ display: "block", margin: "0 auto" }}
              onClick={handleSkipLabReportUpload}
            >
              {translate("task.skip.button_text")}
            </ClearButton>
          </div>
        )}
      </StepContent>
      {currentStep !== "upload" && (
        <StepOverlay>
          <UploadLabResultStepContent
            isUploading={isUploading}
            uploadFilesAndShowStep={uploadFilesAndShowStep}
            closeDialog={closeDialog}
          />
        </StepOverlay>
      )}
    </UploadWrapper>
  );
};
